import { useEffect, useRef } from 'react';
import { apiService } from '../services/ApiService';
import { useOrgFeatureFlag } from './useOrgFeatureFlag';

/**
 * Records every meaningful click on the active order so support can replay what
 * the person actually did (see the Trail panel in ChatHeader, admin/debug only).
 *
 * Gated on the `order_click_trail` feature flag (org row > global row, default
 * OFF): while it is off no listener is attached and nothing is sent.
 *
 * One delegated capture-phase listener — no per-component instrumentation. The
 * label comes from `data-track` when a component sets one, otherwise from the
 * element's aria-label or its text. Only labels and `data-*` attributes are
 * recorded, never typed values.
 */
const FLUSH_EVERY_MS = 5000;
const MAX_BUFFER = 20;
const TRAIL_FLAG = 'order_click_trail';
const TRACKABLE = '[data-track],button,a,[role="button"],[role="option"],[role="tab"],select,input[type="checkbox"],input[type="radio"]';

type TrailEvent = { t: number; label: string; path?: string; detail?: Record<string, string> };

export function useClickTrail(orderId: number | null | undefined, sessionId?: string | null) {
  const enabled = useOrgFeatureFlag(TRAIL_FLAG);
  const sessionRef = useRef(sessionId);
  sessionRef.current = sessionId;

  useEffect(() => {
    if (!orderId || !enabled) return;

    const buffer: TrailEvent[] = [];
    const flush = () => {
      if (!buffer.length) return;
      const events = buffer.splice(0);
      apiService
        .post(`/api/user/orders/${orderId}/clicks`, { session_id: sessionRef.current || null, events })
        .catch(() => { /* trail is best-effort — never surface to the user */ });
    };

    const onClick = (e: MouseEvent) => {
      const el = (e.target as HTMLElement | null)?.closest?.(TRACKABLE) as HTMLElement | null;
      if (!el) return;
      const label =
        el.dataset.track ||
        el.getAttribute('aria-label') ||
        el.getAttribute('title') ||
        el.textContent?.trim().slice(0, 60) ||
        el.tagName.toLowerCase();
      buffer.push({
        t: Date.now(),
        label,
        path: window.location.pathname + window.location.search,
        detail: Object.keys(el.dataset).length ? { ...(el.dataset as Record<string, string>) } : undefined,
      });
      if (buffer.length >= MAX_BUFFER) flush();
    };

    const onHide = () => { if (document.visibilityState === 'hidden') flush(); };

    // Capture phase so a handler calling stopPropagation can't hide the click.
    document.addEventListener('click', onClick, true);
    document.addEventListener('visibilitychange', onHide);
    const interval = window.setInterval(flush, FLUSH_EVERY_MS);

    return () => {
      document.removeEventListener('click', onClick, true);
      document.removeEventListener('visibilitychange', onHide);
      window.clearInterval(interval);
      flush();
    };
  }, [orderId, enabled]);
}

export default useClickTrail;
